import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ShieldCheck, Handshake, Headphones, Map, Award, Leaf, Phone } from 'lucide-react';
import { SectionHeader } from '@/components/home/CoreSections';
import { homeT } from '@/i18n/homeI18n';
import { logError } from '@/utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = [ShieldCheck, Handshake, Headphones, Map];

export function WhyEurogulf({ lang = 'en' }) {
  const t = homeT(lang).why;
  return (
    <section data-testid="why-section" className="bg-[#F5F5F3] py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader compact chip={t.chip} title={t.title} desc={t.desc} />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
          {t.items.map((w, i) => {
            const Icon = ICONS[i];
            return (
              <div key={w.title} data-testid={`why-card-${i}`} className="bg-white p-6 lg:p-7 transition-colors hover:bg-[#FFF4ED]/70">
                <Icon className="w-5 h-5 text-[#EE5A01]" />
                <h3 className="mt-5 font-heading font-bold text-base text-[#121212]">{w.title}</h3>
                <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {t.awards.map((a) => (
            <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 font-body text-xs text-[#555555]"><Award className="w-3.5 h-3.5 text-[#EE5A01]" /> {a}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LatestNews() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/articles?limit=3`).then(({ data }) => setArticles(data.articles || [])).catch((err) => logError('Home news', err));
  }, []);
  if (articles.length === 0) return null;
  return (
    <section data-testid="news-section" className="bg-[#FAFAFA] py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeader chip="Media Center" title="Latest from the group." />
          <Link to="/media" data-testid="news-view-all" className="btn-outline w-fit">All news <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {articles.map((a) => (
            <Link key={a.id} to={`/media/${a.id}`} data-testid={`news-card-${a.id}`} className="card-light group overflow-hidden">
              <div className="h-44 overflow-hidden bg-[#EEEDE7]">
                {a.image_url ? <img src={a.image_url} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="h-full w-full bg-gradient-to-br from-[#EE5A01] to-[#F17B34]" />}
              </div>
              <div className="p-5">
                <span className="chip-outline">{a.category}</span>
                <h3 className="mt-3 font-heading font-bold text-base text-[#121212] leading-snug group-hover:text-[#EE5A01] transition-colors line-clamp-2">{a.title}</h3>
                <p className="mt-2 font-body text-xs text-[#666666]">{new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SustainabilityTeaser() {
  return (
    <section data-testid="sustainability-teaser" className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/sustainability" className="group flex flex-col md:flex-row items-start md:items-center gap-6 rounded-2xl border border-black/5 bg-[#F5F5F3] p-6 md:p-8 hover:border-[#EE5A01]/40 transition-colors">
          <span className="grid h-14 w-14 place-items-center rounded-xl bg-[#121212] text-[#EE5A01] flex-shrink-0"><Leaf className="w-6 h-6" /></span>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-[#121212]">Moving towards a greener UAE</h3>
            <p className="mt-1 font-body text-sm text-[#666666]">Hybrid and electric fleet growth, telematics-driven efficiency and responsible vehicle life-cycles, aligned with UAE Net Zero 2050.</p>
          </div>
          <span className="inline-flex items-center gap-1 font-heading font-bold text-sm text-[#EE5A01] group-hover:gap-2 transition-all">Our roadmap <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </div>
    </section>
  );
}

export function FinalCta({ lang = 'en' }) {
  const t = homeT(lang).cta;
  return (
    <section data-testid="final-cta" className="relative overflow-hidden bg-[#EE5A01] py-16 sm:py-20">
      <div className="absolute inset-y-0 end-0 w-1/2 bg-[#F17B34] [clip-path:polygon(30%_0,100%_0,100%_100%,0_100%)] rtl:[clip-path:polygon(0_0,70%_0,100%_100%,0_100%)]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <div className="max-w-2xl">
          <p className="font-heading font-black text-sm tracking-[0.2em] uppercase text-white/80">{t.eyebrow}</p>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-white leading-[1.1] tracking-tight">{t.title}</h2>
          <p className="mt-4 font-body text-base text-white/85 max-w-lg">{t.desc}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to={t.quoteHref} data-testid="final-cta-quote" className="btn-secondary">{t.quote} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
          <a href="tel:800364" dir="ltr" data-testid="final-cta-call" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#EE5A01]"><Phone className="w-4 h-4" /> {t.call}</a>
        </div>
      </div>
    </section>
  );
}
